import React, {useState, useEffect, useRef} from 'react';
import IconButton from '@material-ui/core/IconButton';
import SendIcon from '@material-ui/icons/Send';
import CustomScroll from 'react-custom-scroll';
import {v4 as uuidv4} from 'uuid';
import classNames from 'classnames';
import 'components/Shared/CustomScroll.scss';
import './Chat.scss';
import ChatMessage from './ChatMessage';
import EmojisBar from './EmojisBar';
import PseudoModal from './PseudoModal';

const MAX_LENGTH = 300;
const PSEUDO_KEY = 'chat-pseudo';

const Chat = ({onEdit, onValidate, onDelete, onSend, messages, notFull, visible, className, pseudo}) => {
  const [text, setText] = useState('');
  const previousMessages = useRef(messages);
  const [reactionsQueue, setReactionsQueue] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [remainingChars, setRemainingChars] = useState(MAX_LENGTH);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);

  const [askPseudo, setAskPseudo] = useState(false);

  const sessionPseudo = sessionStorage.getItem(PSEUDO_KEY);

  const getPseudo = () => {
    if (pseudo) return pseudo;
    if (sessionPseudo === 'null') return null;
    if (sessionPseudo) return sessionPseudo;
    return null;
  };

  const submitPseudo = customPseudo => {
    setAskPseudo(false);
    sessionStorage.setItem(PSEUDO_KEY, customPseudo);
  };

  const handleSend = () => {
    if (!text.trim()) {
      return;
    }
    onSend({msg: text, side: 'salle', msgType: 'text', from: getPseudo()});
    setText('');
    setRemainingChars(MAX_LENGTH);
    setFocus(false);
  };

  const handleTextInputChange = e => {
    const value = e.target.value.slice(0, MAX_LENGTH);
    setRemainingChars(MAX_LENGTH - value.length);
    setText(value);
    setFocus(true);
  };

  const handleFocus = () => {
    if (!pseudo && (!sessionPseudo || sessionPseudo === null)) {
      setAskPseudo(true);
    }
  };

  const handleBlur = e => {
    setFocus(false);
  };

  const handleEmojiClick = id => {
    onSend({msg: id, msgType: 'emoji', side: 'salle', from: getPseudo()});
  };

  const removeFirstReaction = () => {
    setReactionsQueue([]);
  };

  useEffect(() => {
    if (!messages.length) return;
    if (!initialized) {
      previousMessages.current = messages;
      setInitialized(true);
      return;
    }
    const nbNewMessages = messages.length - previousMessages.current.length;
    const newMessages = nbNewMessages > 0 ? messages.slice(0, nbNewMessages) : [];
    if (newMessages.length) {
      const newReactions = newMessages.filter(m => m.msgType === 'emoji');
      setReactionsQueue(previousReactionsQueue => [...previousReactionsQueue, ...newReactions]);
    }
    previousMessages.current = messages;
  }, [messages, initialized]);

  const isVisible = visible || hover;

  const cleanMessages = messages.filter(m => {
    if (!m) return false;
    if (m.msgType === 'emoji') return false;
    if (m.status === 'deleted') return false;
    return true;
  });
  return (
    <div
      className={classNames('chat', 'container', className, {
        notfull: notFull,
        focus,
        fadein: isVisible,
        fadeout: !isVisible,
      })}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className={classNames('chat-wrapper', className)}>
        {onSend && (
          <div className={classNames('chat', 'form', className)}>
            <div className={classNames('chat', 'input-group', className)}>
              <input
                type="text"
                value={text}
                onChange={handleTextInputChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                className={classNames('chat', 'input', className)}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
              />
              <IconButton aria-label="send message" onClick={handleSend}>
                <SendIcon />
              </IconButton>
            </div>
            <hr className={classNames('chat', 'input-hr', className)} />
            <div className={classNames('chat', 'remaining-chars', className)}>{remainingChars}</div>
          </div>
        )}
        <div className={classNames('chat', 'messages-container', className)}>
          <CustomScroll>
            <div className={classNames('chat', 'messages-container-inner', className)}>
              {cleanMessages.map(message => (
                <ChatMessage
                  key={message.id}
                  className={className}
                  isLeft={message.side !== 'salle'}
                  onEdit={onEdit ? () => onEdit(message) : null}
                  onValidate={onValidate ? body => onValidate({...message, body}) : null}
                  onDelete={onDelete ? () => onDelete(message) : null}
                  from={message.from}
                  body={message.msg}
                  delay={message.delay}
                />
              ))}
            </div>
          </CustomScroll>
        </div>
      </div>
      <EmojisBar
        className={className}
        onEmojiClick={handleEmojiClick}
        reactionsQueue={reactionsQueue}
        removeFirstReaction={removeFirstReaction}
      />
      <PseudoModal open={askPseudo} onSubmit={submitPseudo} />
    </div>
  );
};

export default Chat;
